import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { nieuwUitStaat } from "../lib/slashopdrachten";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Users, Plus, ArrowLeft, Trash2, Shield, UserCog } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import { useTaal } from "@/contexts/TaalContext";
import { PERSOONLIJK, WISSEL_BERICHT, leesActiefTeam, zetActiefTeam } from "@/lib/actiefTeam";


export default function TeamsPage() {
  const { t } = useTaal();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  // SL-a: /team <naam> opent het formulier met de naam ingevuld.
  const locatie = useLocation();
  useEffect(() => {
    const naam = nieuwUitStaat(locatie.state);
    if (naam !== null) { setNewTeamName(naam); setCreateDialog(true); }
  }, [locatie.key, locatie.state]);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  useEffect(() => { fetchTeams(); }, []);

  // K2 (18 okt): welk team actief is, en meelopen als de zijbalk wisselt.
  const [actief, setActief] = useState(PERSOONLIJK);
  useEffect(() => {
    leesActiefTeam().then(setActief).catch(() => {});
    const meelopen = (e) => setActief(e.detail ?? PERSOONLIJK);
    window.addEventListener(WISSEL_BERICHT, meelopen);
    return () => window.removeEventListener(WISSEL_BERICHT, meelopen);
  }, []);
  const maakActief = async (id) => {
    try {
      setActief(await zetActiefTeam(id));
      toast.success(t("Werkruimte gewisseld"));
    } catch { toast.error(t("Werkruimte wisselen mislukt")); }
  };

  const fetchTeams = async () => {
    try {
      const resp = await axios.get(`${API}/teams`);
      setTeams(resp.data);
    } catch { setTeams([]); }
    finally { setLoading(false); }
  };

  const fetchMembers = async (teamId) => {
    try {
      const resp = await axios.get(`${API}/teams/${teamId}/members`);
      setMembers(resp.data);
    } catch { setMembers([]); }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      await axios.post(`${API}/teams`, { name: newTeamName });
      setCreateDialog(false);
      setNewTeamName("");
      fetchTeams();
      toast.success(t("Team aangemaakt"));
    } catch { toast.error(t("Team aanmaken mislukt")); }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await axios.post(`${API}/teams/${selectedTeam.id}/invite`, { email: inviteEmail, role: inviteRole });
      setInviteDialog(false);
      setInviteEmail("");
      fetchMembers(selectedTeam.id);
      toast.success(t("Uitgenodigd"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Uitnodigen mislukt"));
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.id}/members/${memberId}`);
      fetchMembers(selectedTeam.id);
      toast.success(t("Lid verwijderd"));
    } catch { toast.error(t("Lid verwijderen mislukt")); }
  };

  const handleDeleteTeam = async () => {
    // Proef blok-k (19 okt): de backend koppelt het werk los en wist niets (K7a), dus de
    // bevestiging zegt dat, in de taal van het scherm.
    if (!window.confirm(t("Team verwijderen? Al het werk van dit team (gesprekken, projecten, kennisbanken, geheugen, agents en skills) gaat terug naar Persoonlijk. Er wordt niets gewist."))) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.id}`);
      setSelectedTeam(null);
      fetchTeams();
      toast.success(t("Team verwijderd"));
    } catch { toast.error(t("Team verwijderen mislukt")); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><p className="text-muted-foreground">{t("Laden...")}</p></div>;

  return (
    <div className={`flex flex-col h-screen ${BOTTOM_NAV_SPACER}`}>
      <header className="border-b px-4 sm:px-6 py-3 flex items-center gap-4 pt-[calc(0.75rem+var(--veilig-boven))]">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Users className="h-5 w-5" />
        <h1 className="text-lg font-semibold flex-1">{t("Teams")}</h1>
        <Button size="sm" onClick={() => setCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-1" /> {t("Nieuw team")}
        </Button>
      </header>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* DEZELFDE KNIJP ALS OP HET KENNISBANKENSCHERM, en hij is hier gevonden
            doordat de ijking van die reparatie de verboden vorm nog ergens
            anders aantrof (23 augustus 2026). `max-h-40` is 160 pixels en gold
            ALLEEN onder `md`, dus alleen op een telefoon; met een kop erin past
            er ongeveer een regel. Frank zag het op het kennisbankenscherm; hier
            stond precies hetzelfde te wachten. */}
        <div className="w-full md:w-64 border-b md:border-b-0 md:border-r p-4 overflow-y-auto space-y-2 max-h-[60vh] md:max-h-none shrink-0">
          {teams.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">{t("Nog geen teams. Maak er een om samen te werken.")}</p>
          )}
          {teams.map((team) => (
            <button
              key={team.id}
              onClick={() => { setSelectedTeam(team); fetchMembers(team.id); }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                selectedTeam?.id === team.id ? "bg-muted" : "hover:bg-muted/50"
              }`}
            >
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-[hsl(var(--accent))]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{team.name}</p>
                <p className="text-xs text-muted-foreground">{team.member_count === 1 ? t("1 lid") : t("{n} leden", { n: team.member_count })}</p>
              </div>
            </button>
          ))}
        </div>

        <div className="flex-1 p-6 overflow-y-auto">
          {!selectedTeam ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-3">
                <Users className="h-12 w-12 mx-auto text-muted-foreground" />
                <h3 className="text-lg font-medium">{t("Kies een team")}</h3>
                <p className="text-sm text-muted-foreground">{t("Kies een team uit de zijbalk of maak een nieuw.")}</p>
              </div>
            </div>
          ) : (
            <div className="max-w-2xl space-y-6">
              {/* Proef blok-k (19 okt): op 390 pixels viel de verwijderknop buiten beeld;
                  de rij breekt nu af en de knoppen zijn op een telefoon 44 pixels hoog. */}
              <div className="flex flex-wrap items-start justify-between gap-3" data-testid="team-knoppenrij">
                <div className="min-w-0">
                  <h2 className="text-xl font-semibold">{selectedTeam.name}</h2>
                  <p className="text-sm text-muted-foreground">{selectedTeam.member_count === 1 ? t("1 lid") : t("{n} leden", { n: selectedTeam.member_count })} &middot; {t("abonnement {plan}", { plan: selectedTeam.plan })}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {actief === selectedTeam.id ? (
                    <Button variant="outline" size="sm" className="h-11 md:h-8" onClick={() => maakActief(PERSOONLIJK)} data-testid="team-terug-persoonlijk">
                      {t("Terug naar persoonlijk")}
                    </Button>
                  ) : (
                    <Button size="sm" className="h-11 md:h-8" onClick={() => maakActief(selectedTeam.id)} data-testid="team-actief-maken">
                      {t("Actief maken")}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-11 md:h-8" onClick={() => setInviteDialog(true)}>
                    <Plus className="h-4 w-4 mr-1" /> {t("Uitnodigen")}
                  </Button>
                  <Button variant="outline" size="sm" className="h-11 w-11 md:h-8 md:w-auto text-destructive" onClick={handleDeleteTeam}
                          aria-label={t("Team verwijderen")} data-testid="team-verwijderen">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Tabs defaultValue="members">
                <TabsList>
                  <TabsTrigger value="members">{t("Leden")}</TabsTrigger>
                </TabsList>
                <TabsContent value="members" className="space-y-2 pt-4">
                  {members.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-lg border">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                        {(m.display_name || m.email || "?")[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{m.display_name || t("Onbekend")}</p>
                        <p className="text-xs text-muted-foreground truncate">{m.email || ""}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs capitalize bg-muted px-2 py-0.5 rounded flex items-center gap-1">
                          {m.role === "admin" ? <Shield className="h-3 w-3" /> : <UserCog className="h-3 w-3" />}
                          {m.role}
                        </span>
                        {m.role !== "admin" && (
                          <Button variant="ghost" size="icon" className="tap-target h-7 w-7 text-destructive" onClick={() => handleRemoveMember(m.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {members.length === 0 && (
                    <p className="text-sm text-muted-foreground">{t("Geen leden gevonden")}</p>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}
        </div>
      </div>

      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Team aanmaken")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>{t("Teamnaam")}</Label>
            <Input value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} placeholder={t("Mijn team")} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>{t("Annuleren")}</Button>
            <Button onClick={handleCreateTeam}>{t("Aanmaken")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Lid uitnodigen")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>{t("E-mailadres")}</Label>
            <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder={t("collega@voorbeeld.nl")} type="email" />
            <Label>{t("Rol")}</Label>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="member">{t("Lid")}</option>
              <option value="admin">{t("Beheerder")}</option>
              <option value="viewer">{t("Kijker")}</option>
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)}>{t("Annuleren")}</Button>
            <Button onClick={handleInvite}>{t("Uitnodigen")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MobileBottomNav />
    </div>
  );
}
